// shared logic between MoveComined/mobile.vue & Disclaimer/desktop.vue
import { isUspsProd, isUspsProdInternal } from '@mgo/core/src/environment';
import { attachFieldErrors } from '@mgo/core/src/validations/stringValidator';
import {
  mapErrors,
  objectIsValid,
} from '@mgo/core/src/validations/utils';
import { object } from 'yup';
import errorMessages from '../../assets/data/error_messages.json';
import pageView from '../../mixins/pageView';
import handleChoice from '../../utils/analytics';
import validators from './validations';

/**
 * Maps a boolean field from api response, to a radio value
 * @param {Boolean} value
 * @param {String}
 */
const mapBooleanToRadios = (value) => (value ? 'YES' : 'NO');

export default {
  mixins: [pageView],
  data() {
    return {
      privacyActFullContent: false,
      offersChoice: false,
      autofillChoice: false,
      fieldErrors: [],
      improvedFieldErrors: {},
      dirty: null,
      hidePrivacyActContent: true, // <--
      // Stephen will delete above when he completes ticket for Desktop
      hideCoaPrivacyActContent: true,
      hideIdPrivacyActContent: true,
      lazy: true,
      showBusinessAlias: false,
      medUpdated: false, // start date updated
      sdUpdated: false, // stop date updated

      // coa
      dcoa: false,
      billingAddressType: '',
      businessName: '',
      multipleAddressesComponent: '',
      businessAliases: [],
      businessAlias1: '',
      businessAlias2: '',
      businessAlias3: '',
      businessAlias4: '',
      informedDelivery: false,
      forwardType: '',
      moverType: '',
      startDate: '',
      stopDate: '',
      ivsProofingCompleted: false,

      // mover
      firstName: '',
      middleName: '',
      lastName: '',
      suffix: '',
      email: '',
      phoneNumber: '',
      phoneType: 'MOBILE',
      moverUUID: '',

      // address
      oldStreetAddress: '',
      oldCity: '',
      oldState: '',
      oldZipCode: '',
      oldUrbanizationName: '',
      oldPoBoxNumber: '',
      oldAddressUUID: '',
      newStreetAddress: '',
      newCity: '',
      newState: '',
      newZipCode: '',
      newUrbanizationName: '',
      newPoBoxNumber: '',
      newPrivateMailbox: '',
      newAddressUUID: '',
      recentConstruction: false,
      overrideAms: false,

      // enhanced business addresses
      primaryBusiness: true,
      reuseNew: false,
      reuseOld: false,

      // coaCheck modal
      coaCheckModal: false,

      // set when simplify page loads
      simplify: false,
      disableNonBusinessMover: false,
      optInMailReminder: false,
    };
  },
  watch: {
    moverType(currentValue) {
      if (currentValue !== 'BUSINESS') {
        this.showBusinessAlias = false;
      }
    },
    form(newValue, oldValue) {
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue) && this.dirty !== null) {
        const changedValue = Object.keys(newValue)
          // we want to trigger the first interaction event for truthy or true -> false values
          // resolves bug on privacy act statement toggle MGOD-4581
          .filter((key) => newValue[key] || newValue[key] === false)
          .find((key) => oldValue[key] !== newValue[key]);
        this.trackFirstEvent(changedValue, 'input');
        this.dirty = true;
      }
    },
    newAddressString(newValue, oldValue) {
      if (oldValue.length && newValue !== oldValue) {
        this.recentConstruction = false;
        this.overrideAms = false;
        this.newAddressError = '';
        this.newConstructionResetAttempts();
      }
    },
    multipleAddresses(currentValue) {
      if (currentValue !== 'YES') {
        this.primaryBusiness = true;
      }
    },
  },
  computed: {
    addressErrorMap: () => new Map(Object.entries(errorMessages)),
    showNonControlExperience: () => JSON.parse(sessionStorage.getItem('showNonControlExperience')),
    handleNavigation() {
      if (this.simplify) {
        return 'contact-info';
      }
      if (this.forwardType === 'TEMPORARY') {
        return 'verify-info-read';
      }
      if (this.moverType === 'BUSINESS') {
        return 'additional-services';
      }
      if ((this.showChoiceExperience || this.showNonControlExperience)
        && this.informedDeliveryEligible) {
        return 'additional-services';
      }
      return 'verify-info-read';
    },
    showChoiceExperience: () => JSON.parse(sessionStorage.getItem('showChoiceExperience')),
    flagAmpersendEnabled() {
      return this.$store.get('flagAmpersendEnabled');
    },
    enhancedBusinessAliasesEnabled() {
      return this.$store.get('config@enhancedBusinessAliasesEnabled');
    },
    enhancedBusinessFlowEnabled() {
      return this.$store.get('config@enhancedBusinessFlowEnabled');
    },
    enhancedBusinessAliasesIncrementCount() {
      return this.$store.get('config@enhancedBusinessAliasesIncrementCount');
    },
    enhancedBusinessAliasesInitialCount() {
      return this.$store.get('config@enhancedBusinessAliasesInitialCount');
    },
    enhancedBusinessAliasesMaxCount() {
      return this.$store.get('config@enhancedBusinessAliasesMaxCount');
    },
    twoFactorAuthEnabled() {
      return (this.moverType !== 'BUSINESS' && this.$store.get('config@twoFactorAuthEnabled'));
    },
    phoneNumLabel() {
      return this.twoFactorAuthEnabled ? 'Mobile Phone Number' : 'Phone Number';
    },
    highIntent() {
      return this.$store.get('highIntent') || false;
    },
    coaDisputesEnabled() {
      return this.$store.get('config@coaDisputesEnabled') || false;
    },
    coaUUID() {
      return this.$coa.coaUUID;
    },
    informedDeliveryEligible() {
      return (
        this.moverType
        && this.moverType !== 'BUSINESS'
        && this.forwardType === 'PERMANENT'
        && this.billingAddressType !== 'OTHER'
      );
    },
    form() {
      const { dirty, ...form } = this.$data;
      return JSON.parse(JSON.stringify(form));
    },
    newAddressString() {
      const {
        newStreetAddress,
        newCity,
        newState,
        newZipCode,
        newUrbanizationName,
        newPrivateMailbox,
        newPoBoxNumber,
      } = this;
      return [
        newStreetAddress,
        newCity,
        newState,
        newZipCode,
        newUrbanizationName,
        newPrivateMailbox,
        newPoBoxNumber,
      ].join('');
    },
    icoaAmount() {
      return this.$store.get('config@icoaAmount') || 125;
    },
    icoaAmountFormatted() {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(this.icoaAmount / 100);
    },
    isProduction() {
      return isUspsProd(window.location.href) || isUspsProdInternal(window.location.href);
    },
    rules() {
      return object().shape(validators);
    },
    errors() {
      const fieldsToValidate = this.simplify ? [
        'moverType',
      ] : [
        'moverType',
        'email',
        'phoneNumber',
        'phoneType',
        'forwardType',
        'startDate',
      ];
      if (this.multipleAddresses !== 'YES') {
        fieldsToValidate.push(...[
          'oldZipCode',
          'oldCity',
          'oldState',
          'oldStreetAddress',
          'newZipCode',
          'newCity',
          'newState',
          'newStreetAddress',
        ]);
      }

      if (!this.simplify) {
        if (this.$options.validateConfirmEmail) {
          fieldsToValidate.push('emailConfirm');
        }

        if (this.forwardType === 'TEMPORARY') fieldsToValidate.push('stopDate');

        if (this.moverType !== 'BUSINESS') {
          fieldsToValidate.push('firstName');
          fieldsToValidate.push('lastName');
        }

        if (this.moverType !== 'BUSINESS' && this.middleName) {
          fieldsToValidate.push('middleName');
        }

        if (this.flagBusinessAliasEnabled && this.moverType === 'BUSINESS'
        && !this.enhancedBusinessAliasesEnabled) {
          fieldsToValidate.push('businessAlias1');
          fieldsToValidate.push('businessAlias2');
          fieldsToValidate.push('businessAlias3');
          fieldsToValidate.push('businessAlias4');
        }
      }
      if (this.multipleAddresses !== 'YES') {
        if (this.showOldPoBox) {
          fieldsToValidate.push('oldPoBoxNumber');
        }

        if (this.oldState === 'PR') {
          fieldsToValidate.push('oldUrbanizationName');
        }

        if (this.showNewPrivateMailbox) {
          fieldsToValidate.push('newPrivateMailbox');
        }

        if (this.showNewPoBox) {
          fieldsToValidate.push('newPoBoxNumber');
        }

        if (this.newState === 'PR') {
          fieldsToValidate.push('newUrbanizationName');
        }

        if (this.moverType === 'FAMILY') {
          fieldsToValidate.push('familySameLastName');
          fieldsToValidate.push('familyRemaining');
        }
      }
      if (this.moverType === 'BUSINESS') {
        fieldsToValidate.push('businessWholeCompany');
        if (this.enhancedBusinessFlowEnabled) {
          fieldsToValidate.push('multipleAddresses');
        }
        if (!this.simplify && this.multipleAddresses !== 'YES') {
          fieldsToValidate.push('businessName');
        }
        if (this.flagBusinessAliasEnabled && !this.enhancedBusinessAliasesEnabled) {
          fieldsToValidate.push('businessAlias1');
          fieldsToValidate.push('businessAlias2');
          fieldsToValidate.push('businessAlias3');
          fieldsToValidate.push('businessAlias4');
        }
      }


      if (this.renderV3Challenge) {
        fieldsToValidate.push('recaptchaChallengeToken');
      }

      return { ...mapErrors(this, fieldsToValidate), ...this.improvedFieldErrors };
    },
    businessAliasValid() {
      let isValid = false;
      if (this.multipleAddresses === 'YES') {
        isValid = this.flagBusinessAliasEnabled
          && this.showBusinessAlias
          && this.moverType === 'BUSINESS'
          ? this.$refs.multipleAddressesComponent?.$refs?.businessAliases?.isValid()
          : true;
      } else {
        isValid = this.flagBusinessAliasEnabled
          && this.showBusinessAlias
          && this.moverType === 'BUSINESS'
          ? this.$refs.businessAliases?.isValid()
          : true;
      }
      return isValid;
    },
    formIsValid() {
      // Simply and MoveCombine share this mixin code.
      // So add exclusion below since Simplify doesn't have business alias on Address-Info page
      if (!this.simplify && this.enhancedBusinessAliasesEnabled) {
        // Need to call extended validation for multi business alias component
        return objectIsValid(this.errors) && this.businessAliasValid;
      }
      return objectIsValid(this.errors);
    },
    flagBusinessAliasEnabled() {
      return this.$store.get('flagBusinessAliasEnabled');
    },
  },
  mounted() {
    // fail safe for page redirects
    this.$store.commit('DISABLE_LOADER');
  },
  methods: {
    deviceProfiling() {
      window.addEventListener('message', this.profilingStatusUpdate, false);
      const profilingOrgId = this.$store.get('config@twoFactorAuthProfilingOrgId');
      const { coaUUID } = this.$coa;
      window.usps.prof('content.usps.com', profilingOrgId, coaUUID);
    },
    async profilingStatusUpdate({ data }) {
      try {
        if (data?.startsWith && data?.startsWith('tmx_profiling_complete')) {
          await this.$store.dispatch('mfa/profilingStatus');
          window.console.log('ivs device profiling status update successfully');
        }
      } catch (e) {
        window.console.log(`failed ivs device profiling status - error message: ${e}`);
      }
    },
    prepareFieldErrors(resp) {
      if (!resp) return null;
      const { raw, xhr } = resp;
      if (!raw && !xhr) return null;
      const obj = xhr.fieldErrors.reduce((accum, { field, message }) => {
        if (field === 'mobilePhone' || field === 'homePhone') {
          Object.assign(accum, { phoneNumber: { message, hasError: true } });
        } else {
          Object.assign(accum, { [field]: { hasError: true, message } });
        }
        return accum;
      }, {});
      this.improvedFieldErrors = { ...this.improvedFieldErrors, ...obj };
      return this.improvedFieldErrors;
    },
    handleChoice,
    setPrimaryBusiness(value) {
      this.primaryBusiness = value;
    },
    async validate() {
      this.improvedFieldErrors = {};
      this.oldAddressError = '';
      this.newAddressError = '';
      this.fieldErrors = [];
      this.lazy = false;
      await this.$nextTick();
      if (this.flagRecaptchaEnabled && !this.renderV3Challenge) {
        const success = await this.recaptchaV3Validate('contact_info');
        this.recaptchaV3Success = success;
      }
      if (this.flagRecaptchaEnabled
        && !this.recaptchaV3Success
        && !this.renderV3Challenge
        && !this.recaptchaChallengeToken) {
        this.lazy = true;
        return this.recaptchaInvokeChallenge();
      }
      if (!this.formIsValid) return this.scrollToFirstError();

      if (this.multipleAddresses === 'YES') {
        // validate multiple addresses
        const multiAddresses = this.$refs.multipleAddressesComponent;
        const busAddressesValid = await multiAddresses?.validateSubmitResponse(null);
        if (!busAddressesValid) {
          return false;
        }
        sessionStorage.setItem('optInMailReminder', this.optInMailReminder);
        if (this.optInMailReminder) {
          this.$coa.emailReminderOptIn();
        } else {
          this.$coa.emailReminderOptOut();
        }
        return multiAddresses?.nextRoute();
      }
      return this.submit();
    },
    attachFieldErrors,
    async scrollToFirstError() {
      const errorHTML = document.querySelector('.errors');
      if (errorHTML !== null) {
        setTimeout(() => {
          errorHTML.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    },
    async hydrateCOA() {
      const { uuid, ...coa } = await this.$coa.coaGet() || {};
      if (uuid) {
        this.billingAddressType = coa.billingAddressType;
        this.businessName = coa.businessName;
        this.dcoa = coa.dcoa;
        this.informedDelivery = coa.informedDelivery;
        this.forwardType = coa.forwardType;
        this.moverType = coa.moverType;
        this.startDate = coa.startDate;
        this.stopDate = coa.stopDate;
        this.ivsProofingCompleted = Boolean(coa.ivsProofingCompleted);
        this.multipleAddresses = coa.multipleAddresses ? 'YES' : coa.multipleAddresses;
        // allow 'NO' and null as possible values for multipleAddresse
        this.multipleAddresses = coa.multipleAddresses === false ? 'NO' : this.multipleAddresses;
      }

      if (coa.moverType === 'FAMILY') {
        this.familySameLastName = coa.famPrompt1ShareLastName !== null
          ? mapBooleanToRadios(coa.famPrompt1ShareLastName)
          : '';
        this.familyRemaining = coa.famPrompt2SameLastNameRemaining !== null
          ? mapBooleanToRadios(coa.famPrompt2SameLastNameRemaining)
          : '';
      }

      if (coa.moverType === 'BUSINESS') {
        this.businessWholeCompany = coa.bizPromptEntireMoving !== null
          ? mapBooleanToRadios(coa.bizPromptEntireMoving)
          : '';
        // add logic to hydrate multipleBusiness radio once BE sends it in coa
        const { aliases } = this.flagBusinessAliasEnabled && await this.$coa.businessAliasGetAll();
        if (aliases?.length) {
          // open businessAlias dropdown
          this.showBusinessAlias = true;

          if (this.enhancedBusinessAliasesEnabled) {
            this.businessAliases = aliases;
          } else {
            this.businessAlias1 = 0 in aliases ? aliases[0].value : '';
            this.businessAlias2 = 1 in aliases ? aliases[1].value : '';
            this.businessAlias3 = 2 in aliases ? aliases[2].value : '';
            this.businessAlias4 = 3 in aliases ? aliases[3].value : '';
          }
        }
      }
    },
    async hydrateMover() {
      const { MOVER } = await this.$coa.personGetAll();
      if (MOVER) {
        this.firstName = MOVER.firstName;
        this.middleName = MOVER.middleName;
        this.lastName = MOVER.lastName;
        this.suffix = MOVER.suffix;
        this.email = MOVER.email;
        // emailConfirm is not supplied by API,
        // hydrating with email prevents blocking the user
        this.emailConfirm = MOVER.email;
        this.phoneNumber = MOVER.mobilePhone || MOVER.homePhone;
        this.phoneType = MOVER.mobilePhone ? 'MOBILE' : 'HOME';
        this.moverUUID = MOVER?.uuid || '';
      }
    },
    async hydrateAddresses() {
      const { addresses } = await this.$coa.addressGetAll();
      const [address1, address2, address3] = addresses;
      [
        address1,
        address2,
        address3,
      ]
        .filter((a) => a)
        .forEach(({ addressType, ...address }) => {
          if (addressType === 'BILLING') {
            return;
          }

          this[`${addressType.toLowerCase()}StreetAddress`] = address.streetAddress;
          this[`${addressType.toLowerCase()}City`] = address.city;
          this[`${addressType.toLowerCase()}State`] = address.state;
          this[`${addressType.toLowerCase()}ZipCode`] = address.zipCode;
          this[`${addressType.toLowerCase()}UrbanizationName`] = address.amsUrbanizationName;
          this[`${addressType.toLowerCase()}PrivateMailbox`] = address.privateMailbox;
          this[`${addressType.toLowerCase()}PoBoxNumber`] = address.poBoxNumber;
          this[`${addressType.toLowerCase()}AddressUUID`] = address?.uuid || '';

          if (addressType === 'NEW') {
            this.overrideAms = address?.overrideAms;
            this.recentConstruction = address?.recentConstruction;
          }
        });
    },

    /**
     * @param {*} query query object for the Vue Router
     */
    async next(query = {}, exception) {
      await this.$nextTick();
      if (this.multipleAddresses === 'YES') {
        // only handle navigation
        const multiAddresses = this.$refs.multipleAddressesComponent;
        await multiAddresses?.nextRoute();
        return;
      }
      // If there are logged errors
      // prevent the next step
      if (this.oldAddressError
        || this.newAddressError
        || this.fieldErrors.length
        || exception) {
        this.$store.commit('DISABLE_LOADER');
        this.scrollToFirstError();
        return;
      }

      // add start date updated to query params
      if (this.medUpdated) {
        Object.assign(query, { medUpdated: this.medUpdated });
      }

      // add stop date updated to query params
      if (this.sdUpdated) {
        Object.assign(query, { sdUpdated: this.sdUpdated });
      }
      if (this.forwardType === 'TEMPORARY') {
        sessionStorage.setItem('informedDelivery', false);
        sessionStorage.removeItem('extension');
        sessionStorage.removeItem('extensionSelected');
      }
      sessionStorage.setItem('forwardType', this.forwardType);
      sessionStorage.setItem('moverType', this.moverType);
      sessionStorage.removeItem('newSession');


      this.$router.push({ name: this.handleNavigation, query });
    },
    /**
     * used to log high intention server event
     * @param {String} interaction - The field or button which triggered this request
     * @param {String} type - This is default to 'click' to ensure less markup in the Vue files
     */
    trackFirstEvent(interaction, type = 'click') {
      if (!this.highIntent) {
        this.$coa.eventTrack('CoaFirstInteraction', { interaction, type });
        this.$store.set('highIntent');
      }
    },
    moreAlias() {
      const bAliases = this.$refs.businessAliases;
      bAliases.moreAlias();
      bAliases.focus();
    },
    async validateForm() {
      this.lazy = false;
      if (!this.formIsValid) {
        await this.$nextTick();
        this.scrollToFirstError();
      }
    },
    updateFieldErrors(fieldErrors) {
      this.fieldErrors = [];
      this.attachFieldErrors(fieldErrors);
      this.prepareFieldErrors(fieldErrors);
    },
    updateFieldErrorsMilitary(error) {
      this.fieldErrors = [
        ...this.fieldErrors,
        { field: error.field, message: error.message },
      ];
      this.errors[error.field].hasError = true;
      this.errors[error.field].message = error.message;
    },
    disableMoverType(value) {
      if (value) {
        this.disableNonBusinessMover = true;
      } else {
        this.disableNonBusinessMover = false;
      }
    },
    updateForwardingFields(values) {
      this.forwardType = values.forwardType ?? '';
      this.startDate = values.startDate ?? '';
      this.stopDate = values.stopDate ?? '';
    },
  },
};
